import { computed, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getTokens } from '@dacr/auth'

import {
  PlanPriceType,
  usePlansQueryQuery,
  type Plan,
  type PlansQueryQuery,
} from '@/shared/graphql/legacy/generated'
import { useBillingStore } from '@/features/billing/store'
import { useUserStore } from '@/shared/stores/user'
import {
  clearPricingCheckout,
  pricingCheckout,
} from '@/features/onboarding/pricingCheckout'

type BillingGroups = NonNullable<
  PlansQueryQuery['plans']
>['groupByBillingCycle']

export const findPricingPlanSelection = (
  groups: BillingGroups,
  productId: string
) => {
  for (const group of groups) {
    const plans = (group?.plans ?? []) as Plan[]

    for (const plan of plans) {
      const planItem = plan.items.find((item) => item.productId === productId)

      if (!planItem) continue

      const billingCycleType = planItem.prices.find(
        (price) => price.type === PlanPriceType.Subscription
      )?.details?.billingCycleType

      return {
        plan,
        planItem,
        billingCycle: {
          type: billingCycleType || 'month',
          description:
            groups.find(
              (billingCycle) =>
                billingCycle?.billingCycleType === billingCycleType
            )?.billingCycleDescription || '',
        },
      }
    }
  }

  return null
}

export const pricingCheckoutEntryRoute = () => {
  if (pricingCheckout.value?.stage !== 'checkout') return '/onboarding'

  return useBillingStore().selectedPlan ? '/onboarding/checkout' : '/onboarding'
}

export function usePricingCheckoutResolver() {
  const route = useRoute()
  const router = useRouter()
  const billingStore = useBillingStore()
  const userStore = useUserStore()

  const enabled = computed(() => pricingCheckout.value?.stage === 'checkout')

  const { result, onError } = usePlansQueryQuery(
    () => ({ accessToken: getTokens().accessToken }),
    { enabled }
  )

  const bail = () => {
    clearPricingCheckout()

    if (route.path === '/onboarding/checkout' && !billingStore.selectedPlan) {
      router.replace('/onboarding')
    }
  }

  onError(() => {
    if (pricingCheckout.value?.stage !== 'checkout') return

    bail()
  })

  watchEffect(() => {
    if (pricingCheckout.value?.stage !== 'checkout') return

    const groups = result.value?.plans?.groupByBillingCycle

    if (!groups) return

    const selection = findPricingPlanSelection(
      groups,
      pricingCheckout.value.productId
    )

    if (!selection) {
      bail()
      return
    }

    billingStore.setPlanData(selection)
    userStore.updateUser({ totalFiles: 0 })
  })
}
